import { JsonObject } from 'type-fest';
import mixpanel from 'mixpanel-browser';
import { IntercomWhitelistedEvents } from './intercom';
import { isInApp } from './utils/platform';
import store from '@/store';
import type { RootState } from '@/store';
import type { User } from '@/reducers/product-management';

type Payload = JsonObject;
// Minimal identity information required for initialization
type UserIdentity = {
  email: string;
  userId: string;
  username: string;
};
type Settings = {
  intercom?: {
    autoBoot?: boolean;
  };
};
type Handler = {
  logEvent(event: string, payload?: Payload, user?: User | null): void;
  onLogin(userIdentity: UserIdentity, settings?: Settings): void;
  onLogout(): void;
  whiteListedEvents?: Set<string>;
};

const tracker = {
  handlers: [] as Handler[],
  register(handler: Handler) {
    this.handlers.push(handler);
  },
  logEvent(event: string, payload?: Payload) {
    // Get current user data from Redux store for all tracking events
    const state = store.getState() as RootState;
    const user = state.user;

    this.handlers.forEach(handler => {
      const trackAll = !handler.whiteListedEvents;
      const trackWhitelist =
        handler.whiteListedEvents && handler.whiteListedEvents.has(event);
      if (trackAll || trackWhitelist) {
        // Pass user profile directly to the handler
        handler.logEvent(event, payload, user);
      }
    });
  },
  onLogin(userIdentity: UserIdentity, settings?: Settings) {
    this.handlers.forEach(handler => handler.onLogin(userIdentity, settings));
  },
  onLogout() {
    this.handlers.forEach(handler => handler.onLogout());
  },
};

const mixpanelHandler: Handler = {
  logEvent(event: string, payload?: Payload, user?: User | null) {
    // Add org_id and enterprise_id for company accounts when available
    const properties: JsonObject = { ...payload };
    if (user && user.default_org_type === 'COMPANY') {
      properties.org_id = user.org_id;
      if (user.enterprise_id) {
        properties.enterprise_id = user.enterprise_id;
      }
    }

    mixpanel.track(event, properties);
  },
  onLogin(userIdentity: UserIdentity) {
    mixpanel.reset();
    mixpanel.identify(userIdentity.username);
    mixpanel.people.set({ email: userIdentity.email });

    // User org data will be handled in logEvent when needed
  },
  onLogout() {
    mixpanel.reset();
  },
};
const intercomHandler: Handler = {
  logEvent(event: string, payload?: Payload, user?: User | null) {
    // Intercom doesn't need the user profile for tracking events
    // as it's already initialized with the user during login
    window.Intercom('trackEvent', event, payload);
  },
  onLogin(userIdentity: UserIdentity, settings) {
    if (!settings?.intercom?.autoBoot) return;
    window.Intercom('boot', {
      app_id: process.env.INTERCOM_APP_ID,
      user_id: userIdentity.username,
      email: userIdentity.email,
      vertical_padding: 120,
      horizontal_padding: 60,
      hide_default_launcher: isInApp(),
    });
  },
  onLogout() {
    window.Intercom('shutdown');
  },
  whiteListedEvents: IntercomWhitelistedEvents,
};

tracker.register(mixpanelHandler);
tracker.register(intercomHandler);

export default tracker;
